import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  CircularProgress, Table, TableBody, TableRow, TableCell, Chip, Alert, Divider,
} from '@mui/material';
import { patientService, MedicationMonitoringReview } from '../../services/patientService';
import { CLINIC_DIALOG_SX, POPUP_COLORS } from '../../constants/popupStyles';
import { formatDateTime } from '../../utils/dateUtils';

interface Props {
  patientId: number | null;
  open: boolean;
  onClose: () => void;
  /** Open the booking flow for an earlier follow-up with the prescribing clinician. */
  onScheduleFollowUp?: () => void;
  /** Called after the review is marked done so the caller can refresh. */
  onDone?: () => void;
}

// Uniform size for the action buttons. The labels are long ("Schedule Earlier
// Follow-Up"), so they wrap inside the button instead of overflowing it.
const actionBtn = {
  flex: 1,
  minWidth: 0,
  minHeight: 44,
  px: 1,
  whiteSpace: 'normal' as const,
  lineHeight: 1.2,
  fontSize: '0.85rem',
};

/**
 * "Medication Monitoring Issue" task popup. Shows the red flags raised by the patient's
 * weekly monitoring questionnaire alongside their contact and prescribing details, with
 * the problematic answers highlighted, and lets a clinician call the patient, bring the
 * follow-up forward, or mark the issue handled.
 */
const MedicationMonitoringIssueDialog: React.FC<Props> = ({
  patientId, open, onClose, onScheduleFollowUp, onDone,
}) => {
  const [loading, setLoading] = useState(false);
  const [review, setReview] = useState<MedicationMonitoringReview | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !patientId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await patientService.getMedicationMonitoring(patientId);
        if (!cancelled) setReview(data);
      } catch {
        if (!cancelled) toast.error('Failed to load medication monitoring details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, patientId]);

  const handleDone = async () => {
    if (!patientId) return;
    setSubmitting(true);
    try {
      await patientService.completeMedicationMonitoring(patientId);
      toast.success('Medication monitoring issue marked as done');
      onDone?.();
      onClose();
    } catch {
      toast.error('Failed to mark the issue as done');
    } finally {
      setSubmitting(false);
    }
  };

  const detailRows: [string, string][] = useMemo(() => {
    if (!review) return [];
    return [
      ['NHS number', review.id_number || '-'],
      ['Current medication & dose', review.current_medication || '-'],
      ['Prescribing clinician', review.prescribing_clinician || '-'],
      ['Questionnaire', review.questionnaire_title || '-'],
      ['Date submitted', review.submitted_at ? formatDateTime(review.submitted_at) : '-'],
    ];
  }, [review]);

  const patientHeader = [
    review?.patient_name,
    review?.hm_id ? `HM ID: ${review.hm_id}` : null,
    review?.phone,
  ].filter(Boolean).join(' · ');

  const labelSx = { fontWeight: 600, border: 0, width: '45%', color: POPUP_COLORS.titleText };
  const valueSx = { border: 0, color: POPUP_COLORS.titleText, whiteSpace: 'pre-line' as const };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: CLINIC_DIALOG_SX.paper }}
    >
      <DialogTitle sx={CLINIC_DIALOG_SX.title}>Medication Monitoring Issue</DialogTitle>
      <DialogContent sx={CLINIC_DIALOG_SX.content}>
        {patientHeader && (
          <Typography
            variant="subtitle1"
            sx={{ color: POPUP_COLORS.titleText, fontWeight: 700, textAlign: 'center', mb: 2 }}
          >
            {patientHeader}
          </Typography>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress sx={CLINIC_DIALOG_SX.spinner} /></Box>
        ) : !review || !review.questionnaire_id ? (
          <Alert severity="info">No medication monitoring issue recorded for this patient.</Alert>
        ) : (
          <>
            <Table size="small">
              <TableBody>
                {detailRows.map(([label, value]) => (
                  <TableRow key={label}>
                    <TableCell sx={labelSx}>{label}</TableCell>
                    <TableCell sx={valueSx}>{value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ color: POPUP_COLORS.titleText, fontWeight: 700, mb: 1 }}>
              Red flag{review.flags.length === 1 ? '' : 's'} detected
            </Typography>
            {review.flags.length === 0 ? (
              <Typography variant="body2" sx={CLINIC_DIALOG_SX.infoText}>None</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {review.flags.map((flag) => (
                  <Chip key={flag.code} label={flag.label} color="error" size="small" />
                ))}
              </Box>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ color: POPUP_COLORS.titleText, fontWeight: 700, mb: 1 }}>
              Answers
            </Typography>
            <Table size="small">
              <TableBody>
                {review.answers.map((answer) => (
                  <TableRow
                    key={answer.question_name}
                    sx={answer.flagged ? { backgroundColor: 'rgba(211, 47, 47, 0.12)' } : undefined}
                  >
                    <TableCell sx={{ ...labelSx, fontWeight: answer.flagged ? 700 : 600 }}>
                      {answer.question_text || answer.question_name}
                    </TableCell>
                    <TableCell sx={{ ...valueSx, fontWeight: answer.flagged ? 700 : 400 }}>
                      {answer.answer.join(', ') || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ ...CLINIC_DIALOG_SX.actions, flexWrap: 'wrap' }}>
        <Button onClick={onClose} disabled={submitting} sx={{ ...CLINIC_DIALOG_SX.cancelButton, ...actionBtn }}>
          Cancel
        </Button>
        {/* TODO: placeholder until the click-to-call work lands. Wire to CallPatientDialog
            (components/dialogs/CallPatientDialog) — review already carries patient_id,
            patient_name and phone, which are exactly its patientId/patientName/patientPhone. */}
        <Button disabled sx={{ ...CLINIC_DIALOG_SX.primaryButton, ...actionBtn }}>
          Call Patient
        </Button>
        <Button
          onClick={onScheduleFollowUp}
          disabled={submitting || !onScheduleFollowUp}
          sx={{ ...CLINIC_DIALOG_SX.primaryButton, ...actionBtn }}
        >
          Schedule Earlier Follow-Up
        </Button>
        <Button
          onClick={handleDone}
          disabled={submitting || !review?.questionnaire_id}
          sx={{ ...CLINIC_DIALOG_SX.primaryButton, ...actionBtn }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MedicationMonitoringIssueDialog;
